import _ from 'lodash'
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc'
import { Button, Tooltip } from '../../imports'
import User from './components/User'
import styles from '../../styles.scss'

// Map the list of users to the User component
const SortableItem = SortableElement(({ value, launchClient }) => (
    <User userId={value} launchClient={launchClient} />
))

// Create the list of sortable items
const SortableList = SortableContainer(({ items, launchClient, layout }) => (
    <ul className={`${styles.userList} ${styles[layout]}`}>
        {items.map((value, index) => (
            // TODO: Find out why I render so often.
            <SortableItem
                key={`item-${value}`}
                index={index}
                launchClient={launchClient}
                value={value}
            />
        ))}
    </ul>
))

class UserList extends Component {
    constructor(props) {
        super(props)

        this.willUnmount = false
        this.launchDelayInterval = undefined
        this.timer = undefined
        this.autoLaunchStartup = undefined
        this.state = { launchClient: undefined }

        // Check if we're supposed to do auto launch on startup
        if (props.autoLaunchOnStartup === true) {
            this.autoLaunchStartup = true
        }
    }

    componentDidUpdate() {
        if (this.autoLaunchStartup) {
            this.runAutoLaunchOnStartup()
        }
    }

    componentWillUnmount() {
        this.willUnmount = true
        clearTimeout(this.timer)
        clearInterval(this.launchDelayInterval)
        this.launchDelayInterval = undefined
        this.timer = undefined
    }

    onSortEnd = ({ oldIndex, newIndex }) => {
        // Get the new sort order of items
        const order = arrayMove([...this.props.sortedUserIds], oldIndex, newIndex)

        // Dispatch an update for the localforage
        this.props.setOrder(_.uniq(order))
    }

    setStateIff = (state) => {
        if (this.willUnmount) return

        this.setState(state)
    }

    runAutoLaunchOnStartup() {
        if (!this.props.playEnabled) {
            console.info(
                'Auto-launch on startup detected. Could not start clients as launcher was updating files.',
            )
            return
        }

        // Set auto-launch as false
        this.autoLaunchStartup = false

        // Disable autorun
        this.props.disableAutoRun()

        // No client to start
        if (this.props.availableIds.length === 0) {
            const message =
                this.props.sortedUserIds.length === 0
                    ? 'Could not start clients as no accounts where logged in.'
                    : 'No account marked for multilaunch, and more than one account logged in. No client to launch.'
            console.info(`Auto-launch on startup detected. ${message}`)
            return
        }

        console.info('Auto-launch on startup detected. Starting clients.')

        // Run the command to start accounts
        this.startClientsMultiLaunch()
    }

    startClientsMultiLaunch() {
        // Get list of userIds that should multi launch
        const accounts = this.props.availableIds

        // Only one account is logged available
        if (accounts.length === 1) {
            if (this.timer === undefined) {
                console.info('Queued 1 account for start-up.')

                // Set timer to return timer to normal
                this.timer = setTimeout(() => {
                    this.setStateIff({ launchClient: undefined })
                    this.timer = undefined
                }, 100)

                // Launch the only client available
                this.setStateIff({
                    launchClient: accounts[0],
                })
            }
            return
        }

        // Launch delay between starting up clients
        const seconds = this.props.launchDelay * 1000

        // Reset the state
        this.setStateIff({ launchClient: undefined })

        // Make sure we only set the inteval if it is not already set
        // Another option would be to clear the running interval first and then set it again
        if (this.launchDelayInterval === undefined) {
            console.info(
                `Queued ${accounts.length} accounts for multi-launch: ${accounts.join(', ')}`,
            )

            // Run for first account without delay
            this.runMultiLaunch(accounts)

            // Set interval which will wait the allocated time
            // then run for any additional accounts
            this.launchDelayInterval = setInterval(() => this.runMultiLaunch(accounts), seconds)
        }
    }

    stopMultiLaunch() {
        clearInterval(this.launchDelayInterval)
        this.setStateIff({ launchClient: undefined })
        this.launchDelayInterval = undefined
        this.running = false
    }

    // eulaStatus
    // -1: No EULA process has been started
    //  0: In the process of deciding what to do with EULA.
    //  1: The EULA is being displayed. Wait for user action.
    //  2: The EULA is invalid, ignore it.
    //  3: The user accepted the EULA.
    //  4: The user declined the EULA.

    runMultiLaunch(accounts) {
        // console.log(`runMultiLaunch: ${this.state.launchClient}, ${this.props.eulaStatus}`)
        if (this.running && (this.props.eulaStatus === 0 || this.props.eulaStatus === 1)) {
            // we want to wait so we simply do nothing
            return
        }

        if (this.running && this.props.eulaStatus === 4) {
            // we want to completely stop
            this.stopMultiLaunch()
            return
        }

        // If we have finished the list of accounts to start
        // kill the interval
        if (accounts.length > 0) {
            this.running = true
            // Retrieve an account from the list
            const account = accounts.shift()

            // Set the state
            this.setStateIff({ launchClient: account })
        } else {
            this.stopMultiLaunch()
        }
    }

    renderPlayButton() {
        // If the server is not ready...
        if (this.props.playEnabled !== undefined && !this.props.playEnabled) return null

        // Check whether we should display the button
        // Only show the play button if there is an logged in inactive account that is either
        // the only logged in account, or is selected for multilaunch
        if (this.props.availableIds.length === 0) return null

        // Get the number of selected accounts for multi-launch
        const selectedAccounts =
            this.props.availableIds.length > 1 ? ` (${this.props.availableIds.length})` : null

        return (
            <Tooltip content={this.props.tooltips.play} style={{ marginRight: '10px' }}>
                <Button inline onClick={() => this.startClientsMultiLaunch()}>
                    {this.props.playText}
                    {selectedAccounts}
                </Button>
            </Tooltip>
        )
    }

    renderExternalAddAccountButton = () => {
        const value = this.props.thirdPartyLogin.providers[0]
        return (
            <Tooltip content={this.props.tooltips.addAccount}>
                <Button
                    target="_blank"
                    onClick={() =>
                        console.info(`Command: Open external login window for ${value.name}`)
                    }
                    href={value.loginUrl
                        .replace('{state}', this.props.ssoData.state)
                        .replace(/{host}/gi, this.props.ssoData.host)
                        .replace('{deviceId}', this.props.deviceId)
                        .replace(/{locale}/gi, this.props.ssoData.locale)}
                >
                    {`+ ${this.props.addAccountText}`}
                </Button>
            </Tooltip>
        )
    }

    renderAddAccountButton = () => {
        if (
            this.props.thirdPartyLogin.enabled &&
            this.props.thirdPartyLogin.providers.length === 1
        ) {
            return this.renderExternalAddAccountButton()
        }

        return (
            <Tooltip content={this.props.tooltips.addAccount}>
                <Button onClick={() => this.props.displayLoginWindow(true)}>
                    {`+ ${this.props.addAccountText}`}
                </Button>
            </Tooltip>
        )
    }

    render() {
        try {
            // If no server is defined, return nothing
            if (this.props.serverId == null || this.props.sortedUserIds == null) return null

            return (
                <React.Fragment>
                    <SortableList
                        items={this.props.sortedUserIds}
                        launchClient={this.state.launchClient}
                        helperClass={styles.moving}
                        lockAxis="y"
                        onSortEnd={this.onSortEnd}
                        pressDelay={this.props.pressDelayMilliseconds}
                        layout={this.props.layout}
                    />
                    <div className={styles.actions}>
                        {this.renderPlayButton()}
                        {this.renderAddAccountButton()}
                    </div>
                </React.Fragment>
            )
        } catch (error) {
            console.info(`Encountered an error while rendering user-list: ${error.message}`)
        }
    }
}

UserList.propTypes = {
    layout: PropTypes.string,
    serverId: PropTypes.string,
    availableIds: PropTypes.arrayOf(PropTypes.string),
    sortedUserIds: PropTypes.arrayOf(PropTypes.string),
    launchDelay: PropTypes.number,
    playEnabled: PropTypes.bool,
    pressDelayMilliseconds: PropTypes.number,
    autoLaunchOnStartup: PropTypes.bool,
    tooltips: PropTypes.shape({
        play: PropTypes.string,
        addAccount: PropTypes.string,
    }),
    playText: PropTypes.string,
    addAccountText: PropTypes.string,
    setOrder: PropTypes.func,
    disableAutoRun: PropTypes.func,
    displayLoginWindow: PropTypes.func,
    eulaStatus: PropTypes.oneOf([-1, 0, 1, 2, 3, 4]),
    thirdPartyLogin: PropTypes.shape({
        enabled: PropTypes.bool,
        providers: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                className: PropTypes.string,
                icon: PropTypes.string,
                loginUrl: PropTypes.string,
                requireDeviceId: PropTypes.bool,
            }),
        ),
    }),
    ssoData: PropTypes.shape({
        host: PropTypes.string,
        locale: PropTypes.string,
        state: PropTypes.string,
    }),
    deviceId: PropTypes.string,
}

export default UserList



// WEBPACK FOOTER //
// ./src/features/Users/components/UserList/component.js